import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { DepartmentCreateRequest } from '~/common/entities/Department/DepartmentRequest';
import { Department } from '~/common/entities/Department/DepartmentTypes';
import { unwrap } from '../common/apiCommons';
import axios, { AxiosError } from 'axios';

export enum DepartmentKeys {
  DEPARTMENT = 'DEPARTMENT',
}

const queries = {
  getDepartments() {
    return axios.get<Department[]>(`/departments/`).then(unwrap);
  },
};

const mutations = {
  createDepartment(values: DepartmentCreateRequest): Promise<Department> {
    return axios.post<Department>('/department/', values).then(unwrap);
  },
};

const hooks = {
  useDepartmentsQuery<T = Department[]>(
    options?: UseQueryOptions<Department[], AxiosError, T>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [DepartmentKeys.DEPARTMENT],
      queryFn: queries.getDepartments,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([DepartmentKeys.DEPARTMENT]),
    ];
    return QueryResultAndInvalidate;
  },
  useCreateDepartment() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.createDepartment,
      onSuccess: async (data) => {
        queryClient.setQueryData(
          [DepartmentKeys.DEPARTMENT],
          (old: Department[] | undefined) => [...(old ?? []), data],
        );
        await queryClient.invalidateQueries([DepartmentKeys.DEPARTMENT]);
      },
    });
  },
};

const DepartmentApi = {
  queries,
  mutations,
  hooks,
};

export default DepartmentApi;
